import Service from '../service';

export default class Auth {
    public verified: string | null = null;

    public timestamp: number = 0;
    public status: boolean = false;
    public loading: any = null;
    public session: any = {};
    public context: any = null;

    constructor(public service: Service) { }

    private apiBaseUrl() {
        if (typeof window === 'undefined') return '/api/v1';

        const configured = (window as any).__WOONGBIN_CORE_API_BASE_URL__;
        const meta = document.querySelector(
            'meta[name="woongbin-core-api-base-url"]'
        ) as HTMLMetaElement | null;
        const base = configured || meta?.content || '/api/v1';
        const parsed = new URL(String(base), window.location.origin);
        const loopback = ['127.0.0.1', '::1', 'localhost'].indexOf(parsed.hostname) >= 0;
        if (
            (parsed.protocol !== 'https:' && !(loopback && parsed.protocol === 'http:')) ||
            parsed.username || parsed.password || parsed.search || parsed.hash ||
            parsed.pathname.replace(/\/$/, '') !== '/api/v1'
        ) {
            throw new Error('Core API base URL 설정이 유효하지 않습니다.');
        }
        return parsed.origin + '/api/v1';
    }

    private async parseResponse(response: Response, fallback: string) {
        const contentType = response.headers.get('content-type') || '';
        let payload: any = null;
        if (response.status !== 204 && contentType.indexOf('application/json') >= 0) {
            payload = await response.json();
        }

        if (!response.ok) {
            const message = (
                payload?.error?.message
                || payload?.data?.message
                || fallback
            );
            const error: any = new Error(message);
            error.status = response.status;
            error.code = payload?.error?.code || 'REQUEST_FAILED';
            error.details = payload?.error?.details || [];
            error.traceId = payload?.trace_id || null;
            throw error;
        }

        return payload;
    }

    private async coreRequest(path: string, options: RequestInit = {}) {
        const headers = new Headers(options.headers || {});
        headers.set('Accept', 'application/json');

        const response = await fetch(this.apiBaseUrl() + path, {
            ...options,
            headers,
            credentials: 'include'
        });
        return this.parseResponse(response, 'Core API 요청을 처리하지 못했습니다.');
    }

    private async legacyRequest(path: string, options: RequestInit = {}) {
        const headers = new Headers(options.headers || {});
        headers.set('Accept', 'application/json');

        const response = await fetch(path, {
            ...options,
            headers,
            credentials: 'same-origin'
        });
        return this.parseResponse(response, '관리자 인증 요청을 처리하지 못했습니다.');
    }

    private redirectWithinOrigin(target: any) {
        if (typeof window === 'undefined') return;

        const raw = String(target || '');
        if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) {
            window.location.assign('/');
            return;
        }

        const destination = new URL(raw, window.location.origin);
        if (destination.origin !== window.location.origin) {
            window.location.assign('/');
            return;
        }
        window.location.assign(
            destination.pathname + destination.search + destination.hash
        );
    }

    private useGuestSession() {
        this.verified = null;
        this.loading = true;
        this.timestamp = new Date().getTime();
        this.session = {};
        this.context = null;
        this.status = false;
        return this;
    }

    private useLegacySession(session: any) {
        if (!session || !session.id) return this.useGuestSession();

        this.verified = 'legacy/local';
        this.loading = true;
        this.timestamp = new Date().getTime();
        this.session = { ...session };
        this.context = {
            auth_strategy: 'legacy/local',
            legacy_role: session.role || '',
            user: {
                id: session.id,
                email: session.email || '',
                display_name: session.name || session.email || '',
                role: session.role || '',
                status: 'ACTIVE'
            },
            platform_roles: []
        };
        this.status = true;
        return this;
    }

    private async csrfToken() {
        const payload = await this.coreRequest('/auth/csrf');
        const token = payload?.data?.csrf_token;
        if (!token) throw new Error('CSRF 토큰을 발급받지 못했습니다.');
        return token;
    }

    public async core(path: string, options: RequestInit = {}) {
        return this.coreRequest(path, options);
    }

    public async csrf() {
        return this.csrfToken();
    }

    public isEmbedded() {
        if (typeof window === 'undefined') return false;
        return window.self !== window.top;
    }

    public async requestIframeStorageAccess(): Promise<boolean | null> {
        if (!this.isEmbedded() || typeof document === 'undefined') return true;

        const storageDocument: any = document;
        if (typeof storageDocument.requestStorageAccess === 'function') {
            try {
                await storageDocument.requestStorageAccess();
                return true;
            } catch (error) {
                if (typeof storageDocument.hasStorageAccess === 'function') {
                    try {
                        return await storageDocument.hasStorageAccess();
                    } catch (checkError) {
                        return false;
                    }
                }
                return false;
            }
        }

        if (typeof storageDocument.hasStorageAccess === 'function') {
            try {
                return await storageDocument.hasStorageAccess();
            } catch (error) {
                return false;
            }
        }

        return null;
    }

    public async logout() {
        await this.legacyRequest('/auth/logout', { method: 'POST' });
        return this.useGuestSession();
    }

    public async init() {
        try {
            const payload = await this.legacyRequest('/auth/check');
            return this.useLegacySession(payload?.data?.session);
        } catch (e) {
            return this.useGuestSession();
        }
    }

    public async update() {
        while (this.loading === null) {
            await this.service.render(100);
        }

        const diff = new Date().getTime() - this.timestamp;
        if (diff > 1000 * 60) {
            this.loading = null;
            await this.init();
        }
    }

    public check: any = new Proxy(((root) => {
        const obj: any = () => root.status;
        obj.root = root;
        return obj;
    })(this), {
        get(target, propKey) {
            const propValue: any = target.root.session[propKey];

            return (values: any = null) => {
                if (values === null) {
                    return propValue !== undefined && propValue !== null;
                }

                const accepted = typeof values === 'string' ? [values] : values;
                if (!Array.isArray(accepted)) return accepted === propValue;
                if (Array.isArray(propValue)) {
                    return propValue.some((value: any) => accepted.indexOf(value) >= 0);
                }
                return accepted.indexOf(propValue) >= 0;
            };
        }
    });

    public allow: any = new Proxy(((root) => {
        const obj: any = (redirect: any = null) => {
            if (root.check()) return true;
            if (redirect) root.redirectWithinOrigin(redirect);
            return false;
        };

        obj.root = root;
        obj.check = root.check;
        return obj;
    })(this), {
        get(target, propKey) {
            return (values: any = null, redirect: any = null) => {
                if (target.check[propKey](values)) return true;
                if (redirect) target.root.redirectWithinOrigin(redirect);
                return false;
            };
        }
    });

    public hash(password: string = '') {
        return this.service.crypto.SHA256(password).toString();
    }
}
